import React from 'react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { GripVertical, Loader2 } from 'lucide-react';

// Protected columns that cannot be dragged
const DEFAULT_PROTECTED_COLUMNS = ['marker', 'actions'];

/**
 * DataTableHeader - Reusable table header with sorting, drag & drop, and resize
 * 
 * Features:
 * - Sort indicators with click to sort
 * - Drag & drop column reordering
 * - Column resize handles
 * - Select all checkbox
 */
export function DataTableHeader({
  columns,
  protectedColumns = DEFAULT_PROTECTED_COLUMNS,
  // Sorting
  sortColumn,
  sortDirection,
  onColumnSort,
  // Drag & Drop
  draggedColumn,
  dragOverColumn,
  onDragStart,
  onDragOver,
  onDragEnd,
  // Resize
  onResizeStart,
  // Selection
  showSelectAll = true,
  isAllSelected,
  isSomeSelected,
  onToggleSelectAll,
  // Column width helpers
  getColumnWidth,
  getMinColumnWidth,
  getColumnLabel,
  // Extra columns before dynamic columns
  extraLeadingColumns = null
}) {
  return (
    <TableHeader className="sticky top-0 z-20 bg-slate-900 shadow-sm shadow-slate-800/50">
      <TableRow className="border-slate-800 hover:bg-transparent bg-slate-900">
        {/* Select All Checkbox */}
        {showSelectAll && (
          <TableHead className="text-slate-400 w-10 min-w-[40px]">
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={onToggleSelectAll}
              className={`border-slate-600 ${isSomeSelected && !isAllSelected ? 'data-[state=checked]:bg-blue-500' : ''}`}
              data-testid="select-all-checkbox"
            />
          </TableHead>
        )}
        
        {/* Extra leading columns (e.g., marker) */}
        {extraLeadingColumns}
        
        {/* Dynamic Columns */}
        {columns.map((col) => (
          <TableHead 
            key={col.id} 
            className={`text-slate-400 relative select-none group cursor-pointer hover:bg-slate-800/50 ${
              dragOverColumn === col.id ? 'bg-blue-500/20' : ''
            } ${draggedColumn === col.id ? 'opacity-50' : ''} ${
              sortColumn === col.id ? 'bg-slate-800/70 text-blue-400' : ''
            }`}
            style={{ 
              width: getColumnWidth ? getColumnWidth(col) : col.width,
              minWidth: getMinColumnWidth ? getMinColumnWidth(col.id) : '60px'
            }}
            draggable={!protectedColumns.includes(col.id)}
            onDragStart={(e) => onDragStart && onDragStart(e, col.id)}
            onDragEnd={onDragEnd}
            onDragOver={(e) => onDragOver && onDragOver(e, col.id)}
            onClick={() => !protectedColumns.includes(col.id) && col.id !== 'actions' && onColumnSort && onColumnSort(col.id)}
            title={!protectedColumns.includes(col.id) && col.id !== 'actions' ? `Click to sort by ${col.label}` : ''}
            data-testid={`col-header-${col.id}`}
          >
            <div className="flex items-center gap-1">
              {!protectedColumns.includes(col.id) && (
                <GripVertical className="h-3 w-3 text-slate-600 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
              <span className="truncate">
                {getColumnLabel ? getColumnLabel(col.id, col.label) : col.label}
              </span>
              {/* Sort indicator */}
              {sortColumn === col.id && (
                <span className="ml-1 text-blue-400">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </div>
            {/* Resize handle */}
            {!protectedColumns.includes(col.id) && onResizeStart && (
              <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                onMouseDown={(e) => onResizeStart(e, col.id, col.width)}
              />
            )}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  );
}

/**
 * DataTableRow - Reusable table row with selection and status styling
 */
export function DataTableRow({
  item,
  itemId,
  columns,
  isSelected,
  onToggleSelect,
  getRowStyle,
  renderCell,
  showCheckbox = true,
  extraLeadingCells = null,
  getCellStyle,
  testIdPrefix = 'row'
}) {
  return (
    <TableRow 
      className={`border-slate-800 ${isSelected ? 'ring-1 ring-blue-500' : ''}`}
      style={getRowStyle ? getRowStyle(item) : undefined}
      data-testid={`${testIdPrefix}-${itemId}`}
    >
      {/* Row Selection Checkbox */}
      {showCheckbox && (
        <TableCell className="w-[40px]">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(itemId)}
            className="border-slate-600"
            data-testid={`select-${testIdPrefix}-${itemId}`}
          />
        </TableCell>
      )}
      
      {/* Extra leading cells (e.g., marker, important star) */}
      {extraLeadingCells}
      
      {/* Dynamic Cells */}
      {columns.map((col) => (
        <TableCell 
          key={col.id}
          className={`px-2 py-1 ${col.id === 'remarks' ? 'align-top' : ''}`}
          style={getCellStyle ? getCellStyle(col) : undefined}
          data-testid={`cell-${col.id}-${itemId}`}
        >
          {renderCell(col.id, item)}
        </TableCell>
      ))}
    </TableRow>
  );
}

/**
 * DataTableEmpty - Empty state component
 */
export function DataTableEmpty({ 
  colSpan, 
  message = 'No data available',
  icon: Icon = null,
  className = ''
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className={`text-center py-10 text-slate-500 ${className}`}>
        {Icon && <Icon className="h-12 w-12 text-slate-600 mx-auto mb-4" />}
        {message}
      </TableCell>
    </TableRow>
  );
}

/**
 * DataTableLoading - Loading state component
 */
export function DataTableLoading({ colSpan, className = '' }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className={`text-center py-10 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto" />
      </TableCell>
    </TableRow>
  );
}

/**
 * DataTableWrapper - Container wrapper with mobile scroll hint and sticky header support
 */
export function DataTableWrapper({ 
  children, 
  tableRef,
  showMobileHint = true,
  className = '',
  fitToScreen = true  // Fit to screen on desktop, allow scroll on mobile
}) {
  return (
    <Card className={`bg-slate-900 border-slate-800 overflow-hidden ${className}`}>
      {/* Mobile scroll hint */}
      {showMobileHint && (
        <div className="lg:hidden text-xs text-slate-500 text-center py-1 flex items-center justify-center gap-2">
          <span>←</span>
          <span>Swipe to see more columns</span>
          <span>→</span>
        </div>
      )}
      <div 
        className={`max-h-[calc(100vh-280px)] relative overflow-y-auto ${fitToScreen ? 'lg:overflow-x-hidden overflow-x-auto' : 'overflow-x-auto'}`}
        ref={tableRef} 
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </div>
    </Card>
  );
}

/**
 * DataTable - Full data table component combining all parts
 * 
 * Usage:
 * <DataTable
 *   data={items}
 *   columns={visibleColumns}
 *   loading={loading}
 *   // ... other props
 * />
 */
export function DataTable({
  data,
  columns,
  loading = false,
  emptyMessage = 'No data available',
  emptyIcon = null,
  // ID field
  idField = 'id',
  // Sorting
  sortColumn,
  sortDirection,
  onColumnSort,
  // Drag & Drop
  draggedColumn,
  dragOverColumn,
  onDragStart,
  onDragOver,
  onDragEnd,
  // Resize
  onResizeStart,
  // Selection
  showSelection = true,
  isAllSelected,
  isSomeSelected,
  onToggleSelectAll,
  isSelected,
  onToggleSelect,
  // Styling
  getRowStyle,
  getCellStyle,
  // Rendering
  renderCell,
  // Column helpers
  getColumnWidth,
  getMinColumnWidth,
  getColumnLabel,
  // Protected columns
  protectedColumns = DEFAULT_PROTECTED_COLUMNS,
  // Extra columns
  extraHeaderColumns = null,
  renderExtraRowCells = null,
  // Ref
  tableRef,
  // Mobile
  showMobileHint = false,
  // Test ID
  testIdPrefix = 'row',
  // Class names
  className = '',
  tableClassName = 'data-table compact w-full',
  // Fit to screen without horizontal scroll
  fitToScreen = true
}) {
  const colSpan = columns.length + (showSelection ? 1 : 0) + (extraHeaderColumns ? 1 : 0);

  return (
    <DataTableWrapper tableRef={tableRef} showMobileHint={showMobileHint} className={className} fitToScreen={fitToScreen}>
      <Table className={tableClassName}>
        <DataTableHeader
          columns={columns}
          protectedColumns={protectedColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onColumnSort={onColumnSort}
          draggedColumn={draggedColumn}
          dragOverColumn={dragOverColumn}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onResizeStart={onResizeStart}
          showSelectAll={showSelection}
          isAllSelected={isAllSelected}
          isSomeSelected={isSomeSelected}
          onToggleSelectAll={onToggleSelectAll}
          getColumnWidth={getColumnWidth}
          getMinColumnWidth={getMinColumnWidth}
          getColumnLabel={getColumnLabel}
          extraLeadingColumns={extraHeaderColumns}
        />
        <TableBody>
          {loading ? (
            <DataTableLoading colSpan={colSpan} />
          ) : data.length === 0 ? (
            <DataTableEmpty 
              colSpan={colSpan} 
              message={emptyMessage}
              icon={emptyIcon}
            />
          ) : (
            data.map((item) => (
              <DataTableRow
                key={item[idField]}
                item={item}
                itemId={item[idField]}
                columns={columns}
                isSelected={isSelected ? isSelected(item[idField]) : false}
                onToggleSelect={onToggleSelect}
                getRowStyle={getRowStyle}
                renderCell={renderCell}
                showCheckbox={showSelection}
                extraLeadingCells={renderExtraRowCells ? renderExtraRowCells(item) : null}
                getCellStyle={getCellStyle}
                testIdPrefix={testIdPrefix}
              />
            ))
          )}
        </TableBody>
      </Table>
    </DataTableWrapper>
  );
}

export default DataTable;
